/**
 * S360ConsultContext — Plano CONSULTORIA (S360-CONSULTING).
 *
 * Phase 12.27 — Persistência real no backend + isolamento tenant.
 *
 * Regras:
 *  - Quando o plano do workspace é `S360-CONSULTING`, `hasConsultoria` fica
 *    ATIVO por padrão (RULE 3 do prompt) — sem necessidade de toggle manual.
 *  - Empresas simuladas vivem em `sprints.empresas_simuladas` (backend), com
 *    `owner_workspace_id` obrigatório. O backend filtra estritamente por
 *    workspace → nunca vaza cross-tenant (RULE 1).
 *  - Projetos criados nesse modo carregam `empresa_simulada_id`, permitindo
 *    filtrar as listas de projeto por empresa (RULE 4 - MODE ON).
 *  - Modo OFF: apenas projetos SEM `empresa_simulada_id` são visíveis.
 *  - `ativa_id` (empresa selecionada) permanece em `localStorage` como preferência
 *    de UI por dispositivo (não é cross-tenant sensível).
 */
import { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { useTenant } from "@/context/TenantContext";
import { empresasSimuladasApi } from "@/lib/empresasSimuladasApi";

const LS_ATIVA = "s360_consult_ativa_id_v2";
const S360ConsultCtx = createContext(null);

const PLANO_CONSULTORIA = "S360-CONSULTING";

// Normaliza empresa vinda do backend para o formato consumido pela UI.
// Backend usa `logo_url`; UI (EmpresaAvatar, formulários) lê `logo` desde v10.
// Mantemos ambos os campos para compat com quaisquer consumidores legados.
function _normalizeEmpresa(e) {
  if (!e) return e;
  return { ...e, logo: e.logo_url || e.logo || null };
}

export function S360ConsultProvider({ children }) {
  const { planoCodigo, currentUser, planoCustomizado, hasSprints } = useTenant();
  const [empresas, setEmpresas] = useState([]);
  const [ativaId, setAtivaId] = useState(() => {
    try { return typeof window !== "undefined" ? window.localStorage.getItem(LS_ATIVA) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [enabledOverride, setEnabledOverride] = useState(null); // toggle manual (null = auto por plano)
  const [hydrated, setHydrated] = useState(false);

  // RULE 3 · auto-ATIVO por default se plano CONTÉM CONSULTING
  // Phase 12.29 — Suporta plano composto: `S360-CONSULTING + V360-P1-N1`
  // continua ativando o modo Consultoria. Antes usávamos comparação exata,
  // o que quebrava assim que o cliente adquiria V360 (empresas simuladas
  // sumiam da UI + projetos vinculados desapareciam).
  const hasConsultoria = useMemo(() => {
    if (enabledOverride !== null) return enabledOverride;
    // Plano Customizado (E1b): Projetos contratado libera todo o S360 (nível máximo),
    // sem trava por tier — equivale ao modo Consultoria.
    if (planoCustomizado && hasSprints) return true;
    const partes = String(planoCodigo || "").toUpperCase().split("+").map(s => s.trim());
    return partes.some(p => p === PLANO_CONSULTORIA);
  }, [planoCodigo, enabledOverride, planoCustomizado, hasSprints]);

  // Hidrata do backend quando o modo consultoria fica ATIVO
  const hidratar = useCallback(async () => {
    if (!hasConsultoria) { setEmpresas([]); setHydrated(true); return; }
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const resp = await empresasSimuladasApi.listar();
      const list = Array.isArray(resp?.empresas) ? resp.empresas.map(_normalizeEmpresa) : [];
      setEmpresas(list);
      // Cache dos ids p/ fallback determinístico do write-through (createProjeto),
      // evitando dependência de timing entre contextos.
      try { window.localStorage.setItem("s360_consult_ids_v1", JSON.stringify(list.map(e => e.id))); } catch { /* noop */ }
      // Corrige ativa_id inválida (referência a empresa de outro tenant / removida)
      if (list.length > 0 && !list.some(e => e.id === ativaId)) {
        setAtivaId(list[0].id);
        try { window.localStorage.setItem(LS_ATIVA, list[0].id); } catch { /* noop */ }
      } else if (list.length === 0) {
        setAtivaId(null);
      }
    } catch (e) {
      console.warn("[S360Consult] hidratação falhou:", e?.message || e);
    } finally {
      setLoading(false);
      setHydrated(true);
    }
  }, [hasConsultoria, currentUser?.id, ativaId]);

  useEffect(() => { hidratar(); }, [hasConsultoria, currentUser?.id]);   

  // Persiste ativa_id em localStorage (UI-preference, não sensível)
  useEffect(() => {
    try {
      if (ativaId) window.localStorage.setItem(LS_ATIVA, ativaId);
      else window.localStorage.removeItem(LS_ATIVA);
    } catch { /* noop */ }
  }, [ativaId]);

  const setConsultoria = useCallback((enabled) => {
    setEnabledOverride(!!enabled);
  }, []);

  const createEmpresa = useCallback(async (data) => {
    const nome = (data.nome || "").trim();
    if (!nome) return { ok: false, erro: "Nome é obrigatório." };
    try {
      const nova = _normalizeEmpresa(await empresasSimuladasApi.criar({
        nome,
        data_abertura: data.data_abertura || null,
        responsavel: data.responsavel || null,
        telefone: data.telefone || null,
        email: data.email || null,
        cor: data.cor || null,
        logo_url: data.logo || data.logo_url || null,
      }));
      setEmpresas(prev => [nova, ...prev]);
      if (!ativaId) setAtivaId(nova.id);
      return { ok: true, empresa: nova };
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e?.message || "Erro ao criar empresa";
      if (status === 409) return { ok: false, erro: msg };
      return { ok: false, erro: msg };
    }
  }, [ativaId]);

  const updateEmpresa = useCallback(async (id, patch) => {
    try {
      // Normaliza logo → logo_url no patch enviado ao backend
      const payload = { ...patch };
      if ("logo" in payload && !("logo_url" in payload)) {
        payload.logo_url = payload.logo;
        delete payload.logo;
      }
      const atualizada = _normalizeEmpresa(await empresasSimuladasApi.atualizar(id, payload));
      setEmpresas(prev => prev.map(e => e.id === id ? { ...e, ...atualizada } : e));
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erro ao atualizar empresa";
      return { ok: false, erro: msg };
    }
  }, []);

  const deleteEmpresa = useCallback(async (id) => {
    try {
      await empresasSimuladasApi.excluir(id);
      setEmpresas(prev => {
        const nova = prev.filter(e => e.id !== id);
        if (ativaId === id) setAtivaId(nova[0]?.id || null);
        return nova;
      });
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erro ao excluir empresa";
      return { ok: false, erro: msg };
    }
  }, [ativaId]);

  const setEmpresaAtivaId = useCallback((id) => setAtivaId(id), []);

  const empresaAtiva = useMemo(
    () => empresas.find(e => e.id === ativaId) || null,
    [empresas, ativaId],
  );

  const value = useMemo(() => ({
    hasConsultoria,
    empresas,
    empresaAtivaId: ativaId,
    empresaAtiva,
    loading,
    hydrated,
    setConsultoria,
    createEmpresa,
    updateEmpresa,
    deleteEmpresa,
    setEmpresaAtivaId,
    refetch: hidratar,
  }), [hasConsultoria, empresas, ativaId, empresaAtiva, loading, hydrated,
      setConsultoria, createEmpresa, updateEmpresa, deleteEmpresa, setEmpresaAtivaId, hidratar]);

  return <S360ConsultCtx.Provider value={value}>{children}</S360ConsultCtx.Provider>;
}

export function useS360Consult() {
  const ctx = useContext(S360ConsultCtx);
  if (!ctx) throw new Error("useS360Consult must be used inside <S360ConsultProvider>");
  return ctx;
}
